import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, MessageSquare, X } from 'lucide-react';
import {
  HistoryAuditEventRow,
  HistoryAuditSection,
  HistoryAuditSheet,
} from '@/components/history-audit';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { LARK_ERROR_QUESTIONS } from '@/lib/lark-error-types';
import {
  closeSubmittedLarkErrorInterview,
  dismissLarkErrorInterview,
  recordLarkErrorAnswers,
  subscribeLarkErrorSession,
  type LarkErrorSessionState,
} from '@/lib/lark-error-session';

/** Long enough to read the acknowledgement before the sheet returns to the dashboard. */
export const LARK_ERROR_SUBMITTED_REDIRECT_MS = 2500;

/**
 * Estimating-style right Sheet. Title is Lark — never KAI.
 * Interview is backend LARK_ERROR_QA_PROMPT. Dismissible.
 * Does not start a cab voice session. Altair is TTS sound only.
 */
export function LarkErrorSheet() {
  const [session, setSession] = useState<LarkErrorSessionState>(() => ({
    open: false,
    ended: false,
    submitted: false,
    offTopicRedirects: 0,
    packet: null,
    qaPrompt: null,
  }));
  const [answers, setAnswers] = useState(['', '', '']);
  const [whatExpanded, setWhatExpanded] = useState(true);
  const [askExpanded, setAskExpanded] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendMessage, setSendMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => subscribeLarkErrorSession(setSession), []);

  useEffect(() => {
    if (session.open) {
      setAnswers(['', '', '']);
      setWhatExpanded(true);
      setAskExpanded(true);
      setSending(false);
      setSendMessage(null);
    }
  }, [session.packet?.systemErrorId, session.open]);

  const packet = session.packet;
  const locked = session.ended || !session.open;
  const hasAnswer = answers.some((answer) => answer.trim().length > 0);

  const returnToDashboard = useCallback(() => {
    closeSubmittedLarkErrorInterview();
    navigate('/dashboard');
  }, [navigate]);

  useEffect(() => {
    if (!session.submitted || !session.open) return;
    const timer = window.setTimeout(returnToDashboard, LARK_ERROR_SUBMITTED_REDIRECT_MS);
    return () => window.clearTimeout(timer);
  }, [session.submitted, session.open, returnToDashboard]);

  const handleSend = async () => {
    if (sending || locked) return;
    setSending(true);
    setSendMessage(null);
    const result = await recordLarkErrorAnswers(answers);
    setSending(false);
    if (result === 'send_failed') {
      setSendMessage("Couldn't send your answers. Please try again.");
    } else if (result === 'blocked') {
      setSendMessage("I can't act on that here — describe the error instead.");
    } else if (result === 'redirect') {
      setSendMessage("Let's stay on this error — what were you trying to do?");
    } else if (result === 'empty') {
      setSendMessage('Add an answer before sending.');
    }
  };

  return (
    <HistoryAuditSheet
      open={session.open}
      onOpenChange={(open) => {
        if (!open) dismissLarkErrorInterview();
      }}
      title="Lark"
    >
      {!packet ? null : (
        <div className="space-y-3">
          <HistoryAuditSection
            title="What landed"
            icon={MessageSquare}
            expanded={whatExpanded}
            onToggle={() => setWhatExpanded((v) => !v)}
          >
            <div className="divide-y divide-border/10">
              {packet.transcript.map((turn, index) => (
                <HistoryAuditEventRow
                  key={`${turn.at}-${index}`}
                  title={turn.role === 'lark' ? 'Lark' : 'You'}
                  typeLabel={turn.blocked ? 'noted' : null}
                  description={turn.text}
                  timestamp={turn.at}
                  actorName={turn.role === 'lark' ? 'Lark' : 'You'}
                />
              ))}
            </div>
          </HistoryAuditSection>

          <HistoryAuditSection
            title="This error"
            icon={MessageSquare}
            count={LARK_ERROR_QUESTIONS.length}
            expanded={askExpanded}
            onToggle={() => setAskExpanded((v) => !v)}
          >
            {session.submitted ? (
              <div
                className="flex flex-col items-center gap-3 px-3 py-6 text-center"
                role="status"
              >
                <CheckCircle2 className="h-8 w-8 text-emerald-600" aria-hidden />
                <div>
                  <p className="font-medium">Your report was submitted.</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Thanks — the team will review this error. Taking you back to the
                    dashboard…
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={returnToDashboard}
                  aria-label="Back to dashboard"
                >
                  <X className="mr-1 h-4 w-4" aria-hidden />
                  Back to dashboard
                </Button>
              </div>
            ) : (
              <form
                className="space-y-3 px-3 py-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  void handleSend();
                }}
              >
                {LARK_ERROR_QUESTIONS.map((question, index) => (
                  <div key={question}>
                    <Label htmlFor={`lark-error-q-${index}`} className="mb-1 block text-sm">
                      {question}
                    </Label>
                    <Textarea
                      id={`lark-error-q-${index}`}
                      value={answers[index]}
                      onChange={(event) => {
                        const next = [...answers];
                        next[index] = event.target.value;
                        setAnswers(next);
                      }}
                      rows={2}
                      disabled={locked || sending}
                    />
                  </div>
                ))}
                {sendMessage ? (
                  <p className="text-sm text-destructive" role="alert">
                    {sendMessage}
                  </p>
                ) : null}
                <div className="flex justify-end gap-2 pt-1">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => dismissLarkErrorInterview()}
                  >
                    Close
                  </Button>
                  <Button type="submit" disabled={locked || sending || !hasAnswer}>
                    {sending ? 'Sending…' : 'Send'}
                  </Button>
                </div>
              </form>
            )}
          </HistoryAuditSection>
        </div>
      )}
    </HistoryAuditSheet>
  );
}
